import { ROUTE_ACCESS } from "@plugins/router/router.const.ts";
import ProfileRoutes from "@pages/profile/routes.ts";
import AssignmentAttemptRoutes from "@pages/attempt/assignment-attempt/routes.ts";
import AssessmentAttemptRoutes from "@pages/attempt/assessment-attempt/routes.ts";
import SelfStudyAttemptRoutes from "@pages/attempt/self-study-attempt/routes.ts";
import PersonalLessonAttemptRoutes from "@pages/attempt/personal-lesson-attempt/routes.ts";
import StudentGroupResourceAttemptRoutes from "@pages/attempt/student-group-resource-attempt/routes.ts";
import AlsSessionAttemptRoutes from "@pages/attempt/als-session-attempt/routes.ts";
import AlsSessionViewRoutes from "@pages/attempt/als-session-view/routes.ts";
import AssignmentViewRoutes from "@pages/attempt/assignment-view/routes.ts";
import AssignmentMonitorRoutes from "@pages/attempt/assignment-monitor/routes.ts";
import AssessmentViewRoutes from "@pages/attempt/assessment-view/routes.ts";
import AssessmentMonitorRoutes from "@pages/attempt/assessment-monitor/routes.ts";
import LessonPreviewRoutes from "@pages/attempt/lesson-preview/routes.ts";
import MyDriveRoutes from "@pages/my-drive/routes.ts";
import LessonRoutes from "@pages/lesson/routes.ts";
import NotebookEditorRoutes from "@modules/notebook-editor/notebook-editor-routes.ts";
import PERMISSION from "@configs/permission.ts";
import ROLE from "@configs/role.ts";
import { defineAsyncComponent } from "vue";
import { LOCATION_TYPE_VALUE } from "@/modules/resource-search/resource-search-helper";

const ErrorPage = defineAsyncComponent(() => import("@pages/static/error.vue"));
const Helpdesk = defineAsyncComponent(() => import("@pages/static/Helpdesk.vue"));
const TermsOfUse = defineAsyncComponent(() => import("@pages/static/TermsOfUse.vue"));
const PrivacyStatement = defineAsyncComponent(() => import("@pages/static/PrivacyStatement.vue"));
const DownloadPage = defineAsyncComponent(() => import("@pages/download/download.vue"));
const SelfStudyAttempt = defineAsyncComponent(
  () => import("@pages/attempt/self-study-attempt/SelfStudyAttempt.vue"),
);
const PersonalLessonAttempt = defineAsyncComponent(
  () => import("@pages/attempt/personal-lesson-attempt/PersonalLessonAttempt.vue"),
);
const StudentGroupResourceAttempt = defineAsyncComponent(
  () => import("@pages/attempt/student-group-resource-attempt/StudentGroupResourceAttempt.vue"),
);
const AlsSessionAttempt = defineAsyncComponent(
  () => import("@pages/attempt/als-session-attempt/AlsSessionAttempt.vue"),
);
const AlsSessionView = defineAsyncComponent(
  () => import("@pages/attempt/als-session-view/AlsSessionView.vue"),
);
const AssignmentAttempt = defineAsyncComponent(
  () => import("@pages/attempt/assignment-attempt/AssignmentAttempt.vue"),
);
const AssignmentView = defineAsyncComponent(
  () => import("@pages/attempt/assignment-view/AssignmentView.vue"),
);
const AssignmentEditor = defineAsyncComponent(
  () => import("@pages/assignment/AssignmentEditor.vue"),
);
const QuestionPaper = defineAsyncComponent(() => import("@pages/print-to-scan/QuestionPaper.vue"));
const ResponsePaper = defineAsyncComponent(() => import("@pages/print-to-scan/ResponsePaper.vue"));
const LessonPreview = defineAsyncComponent(
  () => import("@pages/attempt/lesson-preview/LessonPreview.vue"),
);
const CalendarPlanner = defineAsyncComponent(() => import("@pages/calendar/CalendarPlanner.vue"));
const SelfStudy = defineAsyncComponent(() => import("@pages/self-study/SelfStudy.vue"));
const Assignment = defineAsyncComponent(() => import("@pages/assignment/Assignment.vue"));
const Assessment = defineAsyncComponent(() => import("@pages/assessment/Assessment.vue"));
const Lesson = defineAsyncComponent(() => import("@pages/lesson/lesson.vue"));
const UserAdvisory = defineAsyncComponent(() => import("@pages/static/UserAdvisory.vue"));
const Notification = defineAsyncComponent(() => import("@pages/notification/NotificationMain.vue"));
const Profile = defineAsyncComponent(() => import("@pages/profile/UserProfile.vue"));
const MyDrive = defineAsyncComponent(() => import("@pages/my-drive/MyDrivePage.vue"));
const ContentMastery = defineAsyncComponent(
  () => import("@pages/content-mastery/ContentMastery.vue"),
);
const StudentGroup = defineAsyncComponent(() => import("@pages/student-group/StudentGroup.vue"));
const StudentGroupListing = defineAsyncComponent(
  () => import("@pages/student-group/StudentGroupListing.vue"),
);
const StudentGroupLiaisonListing = defineAsyncComponent(
  () => import("@pages/student-group/StudentGroupLiaisonListing.vue"),
);
const CommunityListing = defineAsyncComponent(
  () => import("@pages/student-group/CommunityListing.vue"),
);
const BrowseCommunity = defineAsyncComponent(
  () => import("@pages/student-group/BrowseCommunity.vue"),
);
const PasswordResetAndPasscodeGenerate = defineAsyncComponent(
  () => import("@pages/admin/PasswordResetAndPasscodeGenerate.vue"),
);
const CommunityGallery = defineAsyncComponent(
  () => import("@pages/community-gallery/CommunityGallery.vue"),
);
const StarredResource = defineAsyncComponent(
  () => import("@pages/starred-resource/StarredResource.vue"),
);
const Dashboard = defineAsyncComponent(() => import("@pages/dashboard/DashboardWrapper.vue"));
const Home = defineAsyncComponent(() => import("@pages/home/home.vue"));
const ResetPasswordWithSecurityQuestion = defineAsyncComponent(
  () => import("@pages/account/ResetPasswordWithSecurityQuestion.vue"),
);
const AccountSetup = defineAsyncComponent(() => import("@pages/account/AccountSetup.vue"));
const ResetPassword = defineAsyncComponent(() => import("@pages/account/ResetPassword.vue"));
const ManageAlpTemplate = defineAsyncComponent(
  () => import("@pages/alp-template/ManageAlpTemplate.vue"),
);
const StudentGroupAdministration = defineAsyncComponent(
  () => import("@pages/student-group-admin/StudentGroupAdministration.vue"),
);
const AccountManagement = defineAsyncComponent(
  () => import("@pages/user-admin/account-manager/AccountManagement.vue"),
);
const UserManagement = defineAsyncComponent(
  () => import("@pages/user-admin/ccpm-admin/UserManagement.vue"),
);
const UserAdministration = defineAsyncComponent(
  () => import("@pages/user-admin/school-and-helpdesk-admin/UserAdministration.vue"),
);
const SubjectAdministration = defineAsyncComponent(
  () => import("@pages/subject-admin/SubjectAdministration.vue"),
);
const ReportGeneration = defineAsyncComponent(
  () => import("@pages/report-generation/ReportGeneration.vue"),
);
const ManageFeatureFlag = defineAsyncComponent(
  () => import("@modules/feature-flag/FeatureFlag.vue"),
);
const ManageResource = defineAsyncComponent(
  () => import("@pages/manage-resource/ManageResource.vue"),
);
const ManageDigitalBadge = defineAsyncComponent(
  () => import("@pages/digital-badge/ManageDigitalBadge.vue"),
);
const DigitalBadgeView = defineAsyncComponent(
  () => import("@pages/digital-badge/DigitalBadgeView.vue"),
);
const AppCreate = defineAsyncComponent(() => import("@pages/app/AppCreate.vue"));
const AppEditor = defineAsyncComponent(() => import("@pages/app/AppEditor.vue"));
const ContentMapCreate = defineAsyncComponent(
  () => import("@pages/content-map/ContentMapCreate.vue"),
);
const ContentMapEditor = defineAsyncComponent(
  () => import("@pages/content-map/ContentMapEditor.vue"),
);
const ContentMapCopy = defineAsyncComponent(() => import("@pages/content-map/ContentMapCopy.vue"));
const AlsLandingPage = defineAsyncComponent(() => import("@pages/als/AlsLandingPage.vue"));
const PrintView = defineAsyncComponent(() => import("@modules/print-view/PrintView.vue"));
const LessonPlanPrint = defineAsyncComponent(
  () => import("@pages/print-lesson-plan/LessonPlanPrint.vue"),
);
const AccountAudit = defineAsyncComponent(() => import("@pages/account-audit/AccountAudit.vue"));
const AccountAuditRule = defineAsyncComponent(
  () => import("@pages/account-audit/AccountAuditRule.vue"),
);
const VerifyUsers = defineAsyncComponent(() => import("@pages/account-audit/VerifyUsers.vue"));
const WhitelistWebsiteView = defineAsyncComponent(
  () => import("@pages/whitelist-website/WhitelistWebsiteView.vue"),
);
const WhitelistWebsiteCreate = defineAsyncComponent(
  () => import("@pages/whitelist-website/WhitelistWebsiteCreate.vue"),
);
const WhitelistWebsiteEdit = defineAsyncComponent(
  () => import("@pages/whitelist-website/WhitelistWebsiteEdit.vue"),
);
const EmbedFrame = defineAsyncComponent(
  () => import("@plugins/richtext/plugins/embed-website/EmbedFrame.vue"),
);
const Login = defineAsyncComponent(() => import("@pages/login/Login.vue"));
const PdfView = defineAsyncComponent(() => import("@pages/pdf-view/PdfView.vue"));
const StudyBuddy = defineAsyncComponent(() => import("@pages/study-buddy/StudyBuddy.vue"));
const Notebook = defineAsyncComponent(() => import("@modules/notebook/Notebook.vue"));
const NotebookEditor = defineAsyncComponent(
  () => import("@modules/notebook-editor/NotebookEditor.vue"),
);
const NotebookPrintView = defineAsyncComponent(
  () => import("@pages/print-view/NotebookPrintView.vue"),
);
const UserRecentlyAccessed = defineAsyncComponent(
  () => import("@modules/user-recently-accessed/UserRecentlyAccessed.vue"),
);
const Admin = defineAsyncComponent(() => import("@modules/admin/Admin.vue"));
const MoeLessonOpen = defineAsyncComponent(
  () => import("@modules/editor/components/lesson/MoeLessonOpen.vue"),
);
const MoeLibraryBrowse = defineAsyncComponent(
  () => import("@modules/resource-browse/MoeLibraryBrowsePage.vue"),
);
const MoeLibraryContentMapBrowse = defineAsyncComponent(
  () => import("@modules/resource-browse/MoeLibraryContentMapBrowsePage.vue"),
);
const MoeLibraryModuleBrowse = defineAsyncComponent(
  () => import("@modules/resource-browse/MoeLibraryModuleBrowsePage.vue"),
);
const BrowseLesson = defineAsyncComponent(
  () => import("@modules/resource-browse/BrowseLessonPage.vue"),
);
const ResourceSearch = defineAsyncComponent(
  () => import("@modules/resource-search/ResourceSearchMultiResourceType.vue"),
);
const BrowseSingleResourceTypePage = defineAsyncComponent(
  () => import("@modules/resource-browse/BrowseSingleResourceTypePage.vue"),
);
const BasicSearchSingleResourceTypePage = defineAsyncComponent(
  () => import("@modules/resource-search/BasicSearchSingleResourceTypePage.vue"),
);
const LibrarySearchPage = defineAsyncComponent(
  () => import("@modules/resource-search/LibrarySearchPage.vue"),
);
const LinkedAssignmentsSummary = defineAsyncComponent(
  () => import("@modules/assignment/components/LinkedAssignmentsSummary.vue"),
);

const appRoutes = [
  /**
   * App Pages
   */
  {
    path: "/",
    name: "Home",
    component: Home,
    meta: { title: "page_title_dashboard", access: ROUTE_ACCESS.AUTHENTICATED },
  },
  {
    path: "/home",
    name: "Dashboard",
    component: Dashboard,
    meta: { title: "page_title_dashboard", access: ROUTE_ACCESS.AUTHENTICATED },
  },
  {
    name: "UserRecentlyAccessed",
    component: UserRecentlyAccessed,
    path: "/recently-accessed",
    meta: {
      title: "page_title_recently_accessed",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [
        ROLE.TEACHER,
        ROLE.STUDENT,
        ROLE.CONTENT_APPROVER,
        ROLE.CONTENT_OFFICER,
        ROLE.ALP_ADMIN,
      ],
    },
  },
  {
    name: "MySubjects",
    component: defineAsyncComponent(() => import("@pages/my-subjects/MySubjects.vue")),
    path: "/profile/followed-subject",
    meta: {
      title: "page_title_my_subjects",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER, ROLE.STUDENT],
    },
  },
  {
    path: "/study-buddy",
    name: "StudyBuddy",
    component: StudyBuddy,
    props: (route) => ({
      action: route.query.action,
      profileUserId: route.query.profileUserId,
    }),
    meta: {
      title: "page_title_my_study_buddy_list",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT],
    },
  },
  {
    path: "/profile",
    name: "Profile",
    component: Profile,
    meta: {
      title: "page_title_profile",
      access: ROUTE_ACCESS.AUTHENTICATED,
    },
    children: ProfileRoutes,
  },
  {
    path: "/planner",
    name: "Planner",
    component: CalendarPlanner,
    props: (route) => ({
      eventUuid: route.query.eventUuid,
      tab: route.query.tab,
      isOpenEventDetailSubpage: route.query.isOpenEventDetailSubpage === "true", // route query always return String, and DO NOT OPEN unless is "true"
    }),
    meta: {
      title: "page_title_planner",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER, ROLE.STUDENT],
    },
  },
  {
    path: "/assignment",
    name: "Assignment",
    component: Assignment,
    meta: { title: "page_title_assignment", access: ROUTE_ACCESS.AUTHENTICATED },
  },
  {
    path: "/assessment",
    name: "Assessment",
    component: Assessment,
    meta: { title: "page_title_assessment", access: ROUTE_ACCESS.AUTHENTICATED },
  },
  {
    path: "/notification",
    name: "Notification",
    component: Notification,
    props: (route) => ({
      query: { view: route.query.view, type: route.query.type },
    }),
    meta: { title: "page_title_notification", access: ROUTE_ACCESS.AUTHENTICATED },
  },
  {
    path: "/learning-progress",
    name: "ContentMastery",
    component: ContentMastery,
    props: (route) => ({
      subjectId: route.query.subject,
      levelAndStreamId: route.query.level,
    }),
    meta: {
      title: "page_title_content_mastery",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER, ROLE.STUDENT],
    },
  },
  {
    path: "/als",
    name: "AlsLandingPage",
    component: AlsLandingPage,
    props: (route) => ({
      recommendedTopicUuid: route.query.recommendedTopicUuid,
      recommendedContentMapUuid: route.query.recommendedContentMapUuid,
    }),
    meta: {
      title: "app_side_nav_item_label_adaptive_learning_system",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT],
    },
  },
  {
    path: "/content-map/create",
    name: "ContentMapCreate",
    component: ContentMapCreate,
    props: (route) => ({ isAls: route.query.isAls === "true" }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.CONTENT_APPROVER],
    },
  },
  {
    path: "/content-map/:mode/:uuid",
    name: "ContentMapEditor",
    component: ContentMapEditor,
    props: (route) => ({
      mode: route.params.mode,
      uuid: route.params.uuid,
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.CONTENT_APPROVER, ROLE.CONTENT_OFFICER],
    },
  },
  {
    path: "/content-map/editor/copy/:uuid",
    name: "ContentMapCopy",
    component: ContentMapCopy,
    props: (route) => ({
      contentMapUuid: route.params.uuid,
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.CONTENT_APPROVER],
    },
  },
  {
    path: "/class-group",
    name: "StudentGroupListing",
    component: StudentGroupListing,
    props: true,
    meta: {
      title: "page_title_class_group",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER, ROLE.STUDENT],
    },
  },
  {
    path: "/class-group/liaison",
    name: "StudentGroupLiaisonListing",
    component: StudentGroupLiaisonListing,
    props: true,
    meta: {
      title: "student_group_list_liaison_group_title",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER, ROLE.STUDENT],
    },
  },
  {
    path: "/class-group/view/:collectionUuid",
    name: "StudentGroup",
    component: StudentGroup,
    props: (route) => ({
      tab: route.query.tab,
      collectionUuid: route.params.collectionUuid,
      eventUuid: route.query.eventUuid,
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER, ROLE.STUDENT],
    },
  },
  {
    path: "/community",
    name: "CommunityListing",
    component: CommunityListing,
    props: true,
    meta: {
      title: "page_title_community",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
    },
  },
  {
    path: "/search/community",
    name: "BrowseCommunity",
    component: BrowseCommunity,
    props: true,
    meta: {
      title: "page_title_browse_community",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
    },
  },
  {
    path: "/starred-resource",
    name: "StarredResource",
    component: StarredResource,
    props: true,
    meta: {
      title: "page_title_starred_resource",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER, ROLE.STUDENT],
    },
  },
  {
    path: "/admin/community-gallery",
    name: "CommunityGallery",
    component: CommunityGallery,
    props: true,
    meta: {
      title: "page_title_community_gallery",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.ALP_ADMIN],
    },
  },
  {
    path: "/my-drive/als-session/attempt/:lessonUuid",
    name: "AlsSessionAttemptPageHolder",
    redirect: { name: "AlsSessionAttemptCover" },
    component: AlsSessionAttempt,
    props: (route) => ({
      lessonUuid: route.params.lessonUuid,
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT],
    },
    children: AlsSessionAttemptRoutes,
  },
  {
    path: "/my-drive/als-session/view/:lessonUuid",
    name: "AlsSessionViewPageHolder",
    redirect: { name: "AlsSessionViewCover" },
    component: AlsSessionView,
    props: (route) => ({
      lessonUuid: route.params.lessonUuid,
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER, ROLE.STUDENT],
    },
    children: AlsSessionViewRoutes,
  },
  {
    path: "/my-drive",
    name: "MyDrive",
    props: (route) => ({ folderUuid: route.params.folderUuid, keyword: route.query.keyword }),
    component: MyDrive,
    meta: {
      title: "page_title_my_drive",
    },
    children: MyDriveRoutes,
  },
  {
    path: "/notebook",
    name: "Notebook",
    component: Notebook,
    meta: {
      title: "page_title_notebook",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT],
    },
  },
  {
    path: "/notebook/:notebookUuid",
    name: "NotebookEditorPageHolder",
    redirect: { name: "NotebookEditorCover" },
    component: NotebookEditor,
    props: (route) => ({
      notebookUuid: route.params.notebookUuid,
      notebookId: route.query.notebookId,
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT],
    },
    children: NotebookEditorRoutes,
  },
  {
    path: "/notebook/printview/:notebookUuid",
    component: NotebookPrintView,
    props: (route) => ({
      notebookUuid: route.params.notebookUuid,
    }),
    name: "NotebookPrintView",
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT],
    },
  },
  {
    path: "/resource/manage",
    name: "ManageResource",
    component: ManageResource,
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.CONTENT_APPROVER, ROLE.CONTENT_OFFICER, ROLE.APP_ADMIN],
    },
  },
  {
    path: "/digital-badge/manage",
    name: "ManageDigitalBadge",
    component: ManageDigitalBadge,
    meta: {
      title: "page_title_manage_digital_badge",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.CONTENT_APPROVER, ROLE.CONTENT_OFFICER],
    },
  },
  {
    path: "/digital-badge/view/:digitalBadgeUuid",
    name: "DigitalBadgeView",
    component: DigitalBadgeView,
    meta: {
      title: "page_title_digital_badge",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.CONTENT_APPROVER, ROLE.CONTENT_OFFICER],
    },
  },
  {
    path: "/alp-template",
    name: "ManageAlpTemplate",
    component: ManageAlpTemplate,
    meta: {
      title: "page_title_manage_template",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.ALP_ADMIN],
    },
  },
  {
    path: "/self-study",
    name: "SelfStudy",
    component: SelfStudy,
    meta: {
      title: "page_title_self_study",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT],
    },
  },
  {
    path: "/moe-library/module/open/:lessonUuid/:pathMatch(.*)*",
    name: "MoeLessonOpen",
    component: MoeLessonOpen,
    props: true,
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER, ROLE.STUDENT],
    },
  },
  {
    path: "/moe-library/module/attempt/:lessonUuid",
    name: "SelfStudyAttemptPageHolder",
    redirect: { name: "SelfStudyAttemptCover" },
    component: SelfStudyAttempt,
    props: true,
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT],
    },
    children: SelfStudyAttemptRoutes,
  },
  {
    path: "/my-drive/module/attempt/:lessonUuid",
    name: "PersonalLessonAttemptPageHolder",
    redirect: { name: "PersonalLessonAttemptCover" },
    component: PersonalLessonAttempt,
    props: true,
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT],
    },
    children: PersonalLessonAttemptRoutes,
  },
  {
    path: "/class-group/assessment/attempt/:assignmentUuid",
    name: "AssessmentAttemptPageHolder",
    redirect: { name: "AssessmentAttemptCover" },
    component: AssignmentAttempt,
    props: true,
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT],
    },
    children: AssessmentAttemptRoutes,
  },
  {
    path: "/class-group/assignment/attempt/:assignmentUuid",
    name: "AssignmentAttemptPageHolder",
    redirect: { name: "AssignmentAttemptCover" },
    component: AssignmentAttempt,
    props: true,
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT],
    },
    children: AssignmentAttemptRoutes,
  },
  {
    path: "/class-group/module/attempt/:lessonUuid",
    name: "StudentGroupResourceAttemptPageHolder",
    redirect: { name: "StudentGroupResourceAttemptCover" },
    component: StudentGroupResourceAttempt,
    props: true,
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT],
    },
    children: StudentGroupResourceAttemptRoutes,
  },
  {
    path: "/class-group/assessment/view/:assignmentUuid",
    name: "AssessmentViewPageHolder",
    redirect: { name: "AssessmentViewCover" },
    component: AssignmentView,
    props: true,
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
    },
    children: AssessmentViewRoutes,
  },
  {
    path: "/class-group/assignment/view/:assignmentUuid",
    name: "AssignmentViewPageHolder",
    redirect: { name: "AssignmentViewCover" },
    component: AssignmentView,
    props: true,
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
    },
    children: AssignmentViewRoutes,
  },
  {
    path: "/class-group/assessment/monitor/:assignmentUuid",
    name: "AssessmentMonitorPageHolder",
    component: AssignmentView,
    props: true,
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
    },
    children: AssessmentMonitorRoutes,
  },
  {
    path: "/class-group/assignment/monitor/:assignmentUuid",
    name: "AssignmentMonitorPageHolder",
    component: AssignmentView,
    props: true,
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
    },
    children: AssignmentMonitorRoutes,
  },
  {
    path: "/class-group/module/assign/:lessonUuid?",
    name: "AssignmentEditor",
    component: AssignmentEditor,
    props: (route) => {
      return {
        lessonUuid: route.params.lessonUuid,
        collectionUuids: route.query.collectionUuids,
        assignmentTitle: route.query.assignmentTitle,
        duplicatable: route.query.duplicatable === "true",
        printable: route.query.printable === "true",
        printableAnswers: route.query.printableAnswers === "true",
        reattemptable: route.query.reattemptable === "true",
        pauseAssignment: route.query.pauseAssignment === "true",
        directCreation: route.query.directCreation === "true",
        fromLocation: route.query.fromLocation,
        locationSourceUuid: route.query.locationSourceUuid,
        eventStartDateTime: route.query.eventStartDateTime,
        individualLeaderBoardEnabled: route.query.individualLeaderBoardEnabled,
        teamLeaderBoardEnabled: route.query.teamLeaderBoardEnabled,
      };
    },
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
    },
  },
  {
    path: "/class-group/module/overview/:moduleUuid",
    name: "LinkedAssignmentsSummary",
    component: LinkedAssignmentsSummary,
    props: (route) => ({
      moduleUuid: route.params.moduleUuid,
      sectionId: route.query.sectionId as string,
      activityId: route.query.activityId as string,
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
    },
  },
  {
    path: "/class-group/assignment/printview/:assignmentUuid/module/:moduleUuid/section/:sectionId/activity/:activityId/question-paper",
    name: "QuestionPaper",
    component: QuestionPaper,
    props: (route) => ({
      activityId: route.params.activityId,
      assignmentUuid: route.query.assignmentUuid,
      studentGroupName: route.query.studentGroupName,
      subjectKey: route.query.subjectKey,
      assignmentTitle: route.query.assignmentTitle,
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
    },
  },
  {
    path: "/class-group/assignment/printview/:assignmentUuid/module/:moduleUuid/section/:sectionId/activity/:activityId/response-paper",
    name: "ResponsePaper",
    component: ResponsePaper,
    props: (route) => ({
      activityId: route.params.activityId,
      assignmentUuid: route.query.assignmentUuid,
      assignmentId: route.query.assignmentId,
      studentGroupUuid: route.query.studentGroupUuid,
      studentGroupName: route.query.studentGroupName,
      subjectKey: route.query.subjectKey,
      lessonUuid: route.query.lessonUuid,
      resourceStatus: route.query.resourceStatus,
      assignmentTitle: route.query.assignmentTitle,
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
    },
  },
  {
    // important: preview route must be before lesson editor route
    path: "/:resourceDomain/module/preview/:lessonUuid",
    name: "LessonPreviewPageHolder",
    redirect: { name: "LessonPreviewCover" },
    component: LessonPreview,
    props: true,
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER, ROLE.ALP_ADMIN, ROLE.CONTENT_APPROVER, ROLE.CONTENT_OFFICER],
    },
    children: LessonPreviewRoutes,
  },
  {
    path: "/:resourceDomain/module/printview/:lessonUuid",
    component: PrintView,
    props: (route) => ({
      lessonUuid: route.params.lessonUuid,
      isPrintViewAnswers: route.params.isPrintViewAnswers === "true",
    }),
    name: "PrintView",
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [
        ROLE.TEACHER,
        ROLE.STUDENT,
        ROLE.ALP_ADMIN,
        ROLE.CONTENT_APPROVER,
        ROLE.CONTENT_OFFICER,
      ],
    },
  },
  {
    path: "/:resourceDomain/module/printview/:lessonUuid/module-plan",
    component: LessonPlanPrint,
    props: (route) => ({
      lessonUuid: route.params.lessonUuid,
    }),
    name: "LessonPlanPrint",
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
    },
  },
  {
    path: "/:resourceDomain/module/printview-response/:lessonUuid",
    component: PrintView,
    props: (route) => ({
      lessonUuid: route.params.lessonUuid,
      isPrintViewAnswers: true,
    }),
    name: "PrintViewResponse",
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT],
    },
  },
  {
    // This routes must came after other /:resourceDomain/module route
    // TODO: SLS-11043, to move :lessonUuid after :mode
    path: "/:admin?/:resourceDomain/module/:mode/:status?/:lessonUuid",
    component: Lesson,
    props: true,
    name: "LessonEditor",
    redirect: { name: "EditorCover" },
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER, ROLE.ALP_ADMIN, ROLE.CONTENT_APPROVER, ROLE.CONTENT_OFFICER],
    },
    children: LessonRoutes,
  },
  {
    path: "/app-library",
    name: "AppLibrary",
    component: BrowseSingleResourceTypePage,
    props: () => ({
      keyword: "",
      resource: "APP",
    }),
    meta: {
      title: "page_title_app_library",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER, ROLE.STUDENT],
    },
  },
  {
    path: "/app-library/search",
    name: "AppLibrarySearch",
    component: BasicSearchSingleResourceTypePage,
    props: (route) => ({
      keyword: route.query.keyword,
      resource: "APP",
      sortBy: {
        sortBy: route.query.sortBy ?? "relevance",
        sortByOrder: route.query.sortByOrder ?? "descending",
      },
    }),
    meta: {
      title: "page_title_app_library",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER, ROLE.STUDENT],
    },
  },
  {
    path: "/external-resource",
    name: "ExternalResource",
    component: BrowseSingleResourceTypePage,
    props: () => ({
      keyword: "",
      resource: "WEBSITE",
    }),
    meta: {
      title: "page_title_external_resources",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
    },
  },
  {
    path: "/external-resource/search",
    name: "ExternalResourceSearch",
    component: BasicSearchSingleResourceTypePage,
    props: (route) => ({
      keyword: route.query.keyword,
      resource: "WEBSITE",
      sortBy: {
        sortBy: route.query.sortBy ?? "relevance",
        sortByOrder: route.query.sortByOrder ?? "descending",
      },
    }),
    meta: {
      title: "page_title_external_resources",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
    },
  },
  {
    path: "/find-resource",
    name: "FindResource",
    component: BrowseLesson,
    props: (route) => ({
      config: { bannerLabel: "page_title_find_resources" },
      filter: {
        learningOutcome: route.query.learningOutcome,
        level: route.query.level,
        location: ["COMMUNITY", "MOE"],
        parentTopic: route.query.parentTopic,
        subject: route.query.subject,
        syllabus: route.query.syllabus,
        topic: route.query.topic,
      },
      keyword: "",
      resource: "LESSON",
      sortBy: {
        sortBy: route.query.sortBy ?? "modifiedDate",
        sortByOrder: route.query.sortByOrder,
      },
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
      title: "page_title_find_resources",
    },
  },
  {
    path: "/search",
    name: "ResourceSearch",
    component: ResourceSearch,
    props: (route) => ({
      config: { bannerLabel: "page_title_find_resources" },
      restrictLocation: [LOCATION_TYPE_VALUE.COMMUNITY, LOCATION_TYPE_VALUE.MOE],
      filter: {
        authors: route.query.authors,
        level: route.query.level,
        location: route.query.location,
        resourceType: route.query.resourceType,
        subject: route.query.subject,
        syllabus: route.query.syllabus,
        topic: route.query.topic,
      },
      keyword: route.query.keyword,
      resource: route.query.resource,
      sortBy: {
        sortBy: route.query.sortBy,
        sortByOrder: route.query.sortByOrder,
      },
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER], // Student redirection is handled inside router/index.ts
      title: "page_title_find_resources",
    },
  },
  {
    path: "/community-gallery",
    name: "FindCommunityGallery",
    component: BrowseLesson,
    props: (route) => ({
      config: { bannerLabel: "page_title_find_community_gallery" },
      filter: {
        authors: route.query.authors,
        level: route.query.level,
        location: ["COMMUNITY"],
        subject: route.query.subject,
      },
      keyword: "",
      resource: "LESSON",
      sortBy: {
        sortBy: "modifiedDate",
      },
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
      title: "page_title_find_community_gallery",
    },
  },
  {
    path: "/community-gallery/search",
    name: "CommunityGallerySearch",
    component: LibrarySearchPage,
    props: (route) => ({
      config: { bannerLabel: "page_title_community_gallery" },
      restrictLocation: [LOCATION_TYPE_VALUE.COMMUNITY],
      filter: {
        authors: route.query.authors,
        level: route.query.level,
        resourceType: route.query.resourceType,
        subject: route.query.subject,
        syllabus: route.query.syllabus,
        topic: route.query.topic,
      },
      keyword: route.query.keyword,
      resource: route.query.resource,
      sortBy: {
        sortBy: route.query.sortBy,
        sortByOrder: route.query.sortByOrder,
      },
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
      title: "page_title_community_gallery",
    },
  },
  {
    path: "/moe-library",
    name: "FindMoeLibrary",
    component: MoeLibraryBrowse,
    props: (route) => ({
      config: { bannerLabel: "page_title_find_moe_library" },
      filter: {
        learningOutcome: route.query.learningOutcome,
        level: route.query.level,
        location: ["MOE"],
        parentTopic: route.query.parentTopic,
        subject: route.query.subject,
        syllabus: route.query.syllabus,
        topic: route.query.topic,
      },
      keyword: "",
      resource: "LESSON",
      sortBy: {
        sortBy: "modifiedDate",
      },
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT, ROLE.TEACHER],
      title: "page_title_find_moe_library",
    },
  },
  {
    path: "/moe-library/content-map",
    name: "MoeLibraryContentMap",
    component: MoeLibraryContentMapBrowse,
    props: () => ({
      config: { bannerLabel: "page_title_moe_library" },
      keyword: "",
      resource: "LESSON",
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT, ROLE.TEACHER],
      title: "page_title_moe_library",
    },
  },
  {
    path: "/moe-library/browse-module",
    name: "MoeLibraryModuleBrowse",
    component: MoeLibraryModuleBrowse,
    props: () => ({
      config: { bannerLabel: "page_title_moe_library" },
      keyword: "",
      resource: "LESSON",
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT, ROLE.TEACHER],
      title: "page_title_moe_library",
    },
  },
  {
    path: "/moe-library/search",
    name: "MoeLibrarySearch",
    component: LibrarySearchPage,
    props: (route) => ({
      config: { bannerLabel: "page_title_moe_library" },
      restrictLocation: [LOCATION_TYPE_VALUE.MOE],
      filter: {
        authors: route.query.authors,
        level: route.query.level,
        resourceType: route.query.resourceType,
        subject: route.query.subject,
        syllabus: route.query.syllabus,
        topic: route.query.topic,
      },
      keyword: route.query.keyword,
      resource: route.query.resource,
      sortBy: {
        sortBy: route.query.sortBy,
        sortByOrder: route.query.sortByOrder,
      },
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.STUDENT, ROLE.TEACHER],
      title: "page_title_moe_library",
    },
  },
  {
    path: "/download",
    name: "Download",
    component: DownloadPage,
    props: (route) => ({
      event: route.query.event,
      isAllSchool: route.query.isAllSchool,
      schoolCode: route.query.schoolCode,
      resourceUuid: route.query.resourceUuid,
      reportType: route.query.reportType,
      assignmentPeriod: route.query.assignmentPeriod,
      generationDate: route.query.generationDate,
      resourceType: route.query.resourceType,
      ccpmGroup: route.query.ccpmGroup,
      requestedMonth: route.query.requestedMonth,
      level: route.query.level,
      role: route.query.role,
    }),
    meta: { title: "page_title_download", access: ROUTE_ACCESS.AUTHENTICATED },
  },
  {
    path: "/password/new",
    name: "NewPassword",
    component: AccountSetup,
    props: (route) => ({ token: route.query.token, isStudent: false }),
    meta: { title: "page_title_new_password", access: ROUTE_ACCESS.PUBLIC },
  },
  {
    path: "/password/reset/:userUuid?",
    name: "ResetPassword",
    component: ResetPassword,
    props: (route) => ({
      token: route.query.token,
      mode: "reset",
      userUuid: route.params.userUuid,
    }),
    meta: { title: "page_title_reset_password", access: ROUTE_ACCESS.PUBLIC },
  },
  {
    path: "/password/change",
    name: "ChangePassword",
    component: ResetPassword,
    props: () => ({
      mode: "change",
    }),
    meta: {
      title: "page_title_change_password",
      access: ROUTE_ACCESS.AUTHENTICATED,
      permission: [PERMISSION.FORCE_CHANGE_PASS],
    },
  },
  {
    path: "/password/forget",
    name: "ResetPasswordWithSecurityQuestion",
    component: ResetPasswordWithSecurityQuestion,
    props: (route) => ({
      userUuid: route.query.userUuid,
    }),
    meta: {
      title: "page_title_reset_password_with_security_question",
      access: ROUTE_ACCESS.PUBLIC,
    },
  },
  {
    path: "/account/setup",
    name: "AccountSetup",
    component: AccountSetup,
    props: () => ({
      isStudent: true,
    }),
    meta: {
      title: "page_title_account_setup",
      access: ROUTE_ACCESS.AUTHENTICATED,
      permission: [PERMISSION.ACCOUNT_SETUP],
    },
  },
  {
    path: "/user/advisory",
    name: "UserAdvisory",
    component: UserAdvisory,
    meta: {
      title: "page_title_user_advisory",
      access: ROUTE_ACCESS.AUTHENTICATED,
      permission: [PERMISSION.REQUIRED_ACKNOWLEDGE_ADVISORY_NOTE],
    },
  },
  {
    path: "/admin",
    name: "Admin",
    component: Admin,
    meta: {
      title: "page_title_admin",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [
        ROLE.TEACHER,
        ROLE.SCHOOL_ADMIN,
        ROLE.CONTENT_APPROVER,
        ROLE.CONTENT_OFFICER,
        ROLE.HELPDESK_ADMIN,
        ROLE.ALP_ADMIN,
        ROLE.VLE_ADMIN,
        ROLE.CCPM_ADMIN,
        ROLE.AUTHORITY_ADMIN,
        ROLE.APP_ADMIN,
        ROLE.ACCOUNT_MANAGER,
      ],
    },
  },
  {
    path: "/admin/class-group",
    name: "StudentGroupAdministration",
    component: StudentGroupAdministration,
    meta: {
      title: "page_title_manage_class_group",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.SCHOOL_ADMIN],
    },
  },
  {
    path: "/admin/class-group/reset-password/:groupUuid?",
    name: "PasswordResetAndPasscodeGenerate",
    component: PasswordResetAndPasscodeGenerate,
    props: true,
    meta: {
      title: "admin_by_teacher_manage_password_passcode_title",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.TEACHER],
    },
  },
  {
    path: "/admin/user/manage-account",
    name: "AccountManagement",
    component: AccountManagement,
    meta: {
      title: "page_title_manage_user",
      access: ROUTE_ACCESS.TWO_FA_AUTHENTICATED,
      role: [ROLE.ACCOUNT_MANAGER],
    },
  },
  {
    path: "/admin/user/manage",
    name: "UserManagement",
    component: UserManagement,
    meta: {
      title: "page_title_manage_user",
      access: ROUTE_ACCESS.TWO_FA_AUTHENTICATED,
      role: [ROLE.VLE_ADMIN, ROLE.CCPM_ADMIN, ROLE.AUTHORITY_ADMIN],
    },
  },
  {
    path: "/admin/user",
    name: "UserAdministration",
    component: UserAdministration,
    meta: {
      title: "page_title_manage_user",
      access: ROUTE_ACCESS.TWO_FA_AUTHENTICATED,
      role: [ROLE.SCHOOL_ADMIN, ROLE.HELPDESK_ADMIN],
    },
  },
  {
    path: "/admin/subject",
    name: "SubjectAdministration",
    component: SubjectAdministration,
    meta: {
      title: "page_title_manage_subject",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.SCHOOL_ADMIN],
    },
  },
  {
    path: "/admin/account-audit",
    name: "AccountAudit",
    component: AccountAudit,
    meta: {
      title: "page_title_audit_accounts",
      access: ROUTE_ACCESS.TWO_FA_AUTHENTICATED,
      role: [ROLE.ACCOUNT_MANAGER],
    },
  },
  {
    path: "/admin/account-audit/rule/:ruleUuid",
    name: "AccountAuditRule",
    component: AccountAuditRule,
    meta: {
      title: "page_title_audit_account_rule",
      access: ROUTE_ACCESS.TWO_FA_AUTHENTICATED,
      role: [ROLE.ACCOUNT_MANAGER],
    },
  },
  {
    path: "/admin/user/verify",
    name: "VerifyUsers",
    component: VerifyUsers,
    meta: {
      title: "page_title_verify_users",
      access: ROUTE_ACCESS.TWO_FA_AUTHENTICATED,
      permission: [PERMISSION.AUDIT_RULE_REVIEW_USER],
    },
  },
  {
    path: "/feature-flag/manage",
    name: "ManageFeatureFlag",
    component: ManageFeatureFlag,
    meta: {
      title: "page_title_manage_feature_flags",
      access: ROUTE_ACCESS.TWO_FA_AUTHENTICATED,
      role: [ROLE.VLE_ADMIN],
    },
  },
  {
    path: "/report/generate/:type",
    name: "ReportGeneration",
    component: ReportGeneration,
    props: (route) => ({ type: route.params.type }),
    meta: {
      title: "page_title_report_generation",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.SCHOOL_ADMIN, ROLE.CONTENT_APPROVER, ROLE.VLE_ADMIN],
    },
  },
  {
    path: "/app/create",
    name: "AppCreate",
    component: AppCreate,
    meta: {
      title: "page_title_app",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.APP_ADMIN],
    },
  },
  {
    path: "/app/embed/:uuid",
    name: "EmbededApp",
    component: EmbedFrame,
    props: (route) => ({
      contextId: route.query.contextId,
      resourceLinkId: route.query.resourceLinkId,
      appUrl: route.query.appUrl,
      appuuid: route.params.uuid,
      custom: route.query.custom,
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
    },
  },
  {
    path: "/app/:mode(view|edit)/:uuid",
    name: "AppEditor",
    component: AppEditor,
    props: (route) => ({ mode: route.params.mode, uuid: route.params.uuid }),
    meta: {
      title: "page_title_app",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.APP_ADMIN],
    },
  },
  /**
   * Whitelist Websites
   */
  {
    path: "/whitelist-website/view/:websiteUuid",
    name: "WhitelistWebsiteView",
    component: WhitelistWebsiteView,
    meta: {
      title: "page_title_website",
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.APP_ADMIN],
    },
  },
  {
    path: "/whitelist-website/create",
    name: "WhitelistWebsiteCreate",
    component: WhitelistWebsiteCreate,
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.APP_ADMIN],
    },
  },
  {
    path: "/whitelist-website/edit/:websiteUuid",
    name: "WhitelistWebsiteEdit",
    component: WhitelistWebsiteEdit,
    props: (route) => ({
      websiteUuid: route.params.websiteUuid,
    }),
    meta: {
      access: ROUTE_ACCESS.AUTHENTICATED,
      role: [ROLE.APP_ADMIN],
    },
  },
  {
    path: "/login",
    name: "Login",
    component: Login,
    meta: { title: "login_label", access: ROUTE_ACCESS.NOT_AUTHENTICATED },
  },
  {
    path: "/pdf/view/:uuid",
    name: "PdfView",
    component: PdfView,
    props: (route) => ({
      uuid: route.params.uuid,
      contextType: route.query.contextType,
      contextUuid: route.query.contextUuid,
    }),
    meta: { access: ROUTE_ACCESS.AUTHENTICATED },
  },
  /**
   * Commmon Pages
   */
  {
    path: "/logout", //handled via beforeEach in plugins/router/index.ts
    name: "Logout",
  },
  {
    path: "/privacy-statement",
    name: "PrivacyStatement",
    component: PrivacyStatement,
    meta: { title: "page_title_privacy_statement", access: ROUTE_ACCESS.PUBLIC },
  },
  {
    path: "/terms-of-use",
    name: "TermsofUse",
    component: TermsOfUse,
    meta: { title: "page_title_terms_of_use", access: ROUTE_ACCESS.PUBLIC },
  },
  {
    path: "/helpdesk",
    name: "Helpdesk",
    component: Helpdesk,
    meta: { title: "page_title_helpdesk", access: ROUTE_ACCESS.AUTHENTICATED },
  },
  {
    path: "/helpdesk-ticket",
    name: "HelpdeskTicket",
    component: () => import("@modules/helpdesk-ticket/HelpdeskTicket.vue"),
    meta: { title: "page_title_helpdesk", access: ROUTE_ACCESS.AUTHENTICATED },
  },
  {
    path: "/403",
    name: "Forbidden",
    component: ErrorPage,
    meta: { title: "page_title_forbidden", errorCode: "403", access: ROUTE_ACCESS.PUBLIC },
  },
  {
    path: "/404",
    name: "PageNotFound",
    component: ErrorPage,
    meta: { title: "page_title_not_found", errorCode: "404", access: ROUTE_ACCESS.AUTHENTICATED },
  },
  {
    path: "/app-disabled",
    name: "AppDisabled",
    component: ErrorPage,
    meta: {
      title: "page_title_service_unavailable",
      errorCode: "appdisabled",
      access: ROUTE_ACCESS.AUTHENTICATED,
    },
  },
  {
    path: "/access-denied",
    name: "Access Denied",
    component: ErrorPage,
    meta: { title: "page_title_forbidden", errorCode: "403", access: ROUTE_ACCESS.PUBLIC },
  },
  {},
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
  },
];

// appRoutes must be last because last route entry will direct anything didn't match to 404
const allRoutes = appRoutes;
export default allRoutes;
